import { ReactNode, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { DefaultError } from '@/app/components/Error';
import { ScreenPreloader } from '@/app/components/Preloader';
import { Error404, Error500 } from '@/components/Error';

import { MeetingData, MeetingDataContext } from '@/app/queries/MeetingQuery/context';
import fetchMeeting, { MeetingFetchError } from '@/app/queries/MeetingQuery/fetchMeeting';

export default function withMeetingQuery<T extends object>(
  Component: (props: T) => ReactNode,
) {
  return function ConnectedComponent(props: T) {
    const { hash } = useParams<{ hash: string }>();

    const { status, data, error, refetch } = useQuery<MeetingData>({
      queryKey: ['onsite'],
      queryFn: () => fetchMeeting(hash!),
      retry: 2,
    });

    const onRefetch = useCallback(() => {
      refetch();
    }, [refetch]);

    switch (status) {
      case 'pending':
        return (
          <ScreenPreloader />
        );
      case 'error':
        // Check if the error is a MeetingFetchError to handle status codes
        if (error instanceof MeetingFetchError) {
          const { status } = error;

          if (status === 404) {
            return (
              <Error404
                description="The meeting you're looking for doesn't exist or has been removed."
                error={error}
              />
            );
          } else if (status >= 500) {
            return (
              <Error500
                description="Failed to connect to meeting server. Please try again later."
                error={error}
              />
            );
          } else if (status === 403) {
            return (
              <DefaultError
                status="403"
                title="Access Denied"
                description="You don't have permission to join this meeting."
                error={error}
                onClick={onRefetch} />
            );
          } else if (status === 401) {
            return (
              <DefaultError
                status="401"
                title="Authentication Required"
                description="You need to be authenticated to join this meeting."
                error={error}
                onClick={onRefetch} />
            );
          }
        }

        // Default error handling for other cases
        return (
          <DefaultError
            status="Oops!"
            description='Retrieving meeting data failed.'
            error={error}
            onClick={onRefetch} />
        );
      case 'success':
        return (
          <MeetingDataContext.Provider value={data}>
            <Component {...props} />
          </MeetingDataContext.Provider>
        );
      default: {
        const neverType: never = status;
        throw new Error(`Unhandled status "${neverType}" in withMeetingQuery`);
      }
    }
  };
}
